<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { utils } from '@frontend/web/src/js/utils';

translator.getLanguages();

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  inactive: {
    type: Boolean,
    default: false,
  },
  short: {
    type: Boolean,
    default: false,
  },
  allowedLoyalty: {
    type: Array,
    default: null,
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
  eventType: {
    type: String,
    default: '',
  },
});
const emits = defineEmits(['removeTicket', 'changeLoyalty']);

const isShowDropDownLoyalty = ref(false);
const allowedLoyalty = computed(() => {
  if (props.allowedLoyalty === null) return null;
  return [{ id: 0, categoryId: 0, name: 'Основной' }, ...props.allowedLoyalty];
});
const currentLoyalty = computed(() => {
  if (props.ticket.loyaltyCategoryId === null) return { id: 0, categoryId: 0, name: 'Основной' };
  return allowedLoyalty.value.find((item) => item.id === props.ticket.loyaltyCategoryId);
});

const sectorName = computed(() => {
  const sector = props.ticket.sector.trim();

  if (sector.startsWith('Сектор Ресторан')) {
    return sector.replace(/^Сектор\s+/i, '').trim();
  }

  return sector.includes('Сектор')
    ? sector
    : `${translator.t('main', 'Sector')} ${sector}`;
});

const rowName = computed(() => (
  props.ticket.sector.includes('Ресторан')
    ? `${translator.t('main', 'Table')} ${props.ticket.row}`
    : `${translator.t('main', 'Row')} ${props.ticket.row}`
));

const placeName = computed(() => {
  if (props.ticket.sector.includes('Ресторан')) {
    return `${rowName.value}, ${translator.t('main', 'Place')} ${props.ticket.col}`;
  }

  return `${rowName.value}, ${translator.t('main', 'Place')} ${props.ticket.col}`;
});

function toggleDropDownLoyalty() {
  if (props.isHistory) {
    return;
  }

  isShowDropDownLoyalty.value = !isShowDropDownLoyalty.value;
}

const loyaltyBtn = ref(null);
function clickOutsideDropdown(e) {
  if (e.target === loyaltyBtn.value || e.target.parentElement === loyaltyBtn.value) {
    return;
  }

  isShowDropDownLoyalty.value = false;
}

function changeLoyalty(categoryLoyalty) {
  emits(
    'changeLoyalty',
    {
      ticket: props.ticket,
      categoryId: categoryLoyalty.id,
      priceId: categoryLoyalty.categoryId ?? categoryLoyalty.priceId,
    },
  );

  isShowDropDownLoyalty.value = false;
}
</script>

<template>
  <div class="i-ticket" :class="{ inactive, short }">
    <div class="i-ticket__content">
      <div class="i-ticket__content-top">
        <template v-if="ticket.sector">
          <div class="i-ticket__sector">{{ sectorName }}</div>
          <div class="i-ticket__place">
            {{ placeName }}
          </div>
        </template>
        <template v-else>
          <div class="i-ticket__sector">{{ eventType }}</div>
        </template>
      </div>
      <div class="i-ticket__bottom">
        <div v-if="props.allowedLoyalty?.length" class="i-ticket__loyalty-wrapper">
          <div ref="loyaltyBtn" class="dropdown" :disabled="props.inactive"
            :class="{ active: isShowDropDownLoyalty, dropdown_inactive: isHistory }" @click="toggleDropDownLoyalty">
            <div class="dropdown__title">{{ currentLoyalty.name }}</div>
            <svg class="dropdown__type-svg" height="20" width="20">
              <use v-if="currentLoyalty.id === 0" xlink:href="#main-preferential-category" />
              <use v-else-if="currentLoyalty.id === 1" xlink:href="#child-preferential-category" />
              <use v-else xlink:href="#other-preferential-category" />
            </svg>
            <svg v-if="!props.inactive && !isHistory" class="dropdown__icon-down" height="16" width="16">
              <use xlink:href="#down" />
            </svg>
          </div>
          <div v-if="isShowDropDownLoyalty" v-on-click-outside="clickOutsideDropdown" class="dropdown__list">
            <div v-for="categoryLoyalty in allowedLoyalty" :key="categoryLoyalty.id" class="dropdown__item"
              @click="changeLoyalty(categoryLoyalty)">
              <input v-model="currentLoyalty.id" class="mr-2" type="radio" :value="categoryLoyalty.id">
              {{ categoryLoyalty.name }}
            </div>
          </div>
        </div>
        <div class="i-ticket__bottom-wrapper">
          <div class="i-ticket__place-price">
            {{ utils.formatNumber(ticket.price) }} <span class="ruble">₽</span>
          </div>
        </div>
        <div v-if="isHistory && ticket.isRefunded" class="i-ticket__refunded">
          {{ translator.t("main", "Refunded") }}
        </div>
      </div>
    </div>
    <button v-if="!isHistory" class="i-ticket__remove-button reset-btn" type="button"
      :title="translator.t('main', 'Remove')" @click="$emit('removeTicket', props.ticket)">
      <svg height="15" width="15">
        <use xlink:href="#close" />
      </svg>
    </button>
  </div>
  <slot>
    <div />
  </slot>
</template>

<style scoped lang="scss">
.i-ticket {
  position: relative;
  min-height: 112px;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  &.large {
    min-width: 224px;

    .i-ticket {
      &__content {
        padding: 18px 15px;
      }
    }
  }

  &.short {
    min-height: auto;
    min-width: auto;
    text-align: center;

    .i-ticket {
      &__content {
        font-size: 14px;
        padding: 8px 20px;
        align-items: center;
        min-height: 56px;
        border-radius: 8px;
        border-image: url('../../../../images/site/border-short.svg') 16 / 16px stretch;

        &-top {
          flex-direction: row;
        }
      }

      &__sector,
      &__place {
        margin-bottom: 4px;
        display: inline-block;
        font-weight: 400;
      }

      &__sector::after {
        content: ",\00A0";
      }

      &__place-price {
        text-align: center;
      }
    }
  }

  &.inactive {
    .i-ticket {
      &__remove-button {
        display: none;
      }

      &__bottom {
        align-items: center;
      }
    }
  }

  &__content {
    width: 100%;
    height: 100%;
    min-height: 112px;
    padding: 9px 18px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    font-size: 14px;
    line-height: 130%;
    border-radius: $ticket-border-radius;
    text-align: left;
    background-color: $white;
    border-image: url('../../../../images/site/border.svg') 30 / 30px stretch;

    &-top {
      display: flex;
      flex-direction: column;
    }
  }

  &__sector,
  &__place {
    margin-bottom: 8px;
    white-space: nowrap;
  }

  &__place {
    font-weight: 700;
    text-transform: capitalize;

    &-price {
      font-weight: 700;
      display: block;
    }
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 6px;

    width: 100%;

    .dropdown {
      width: fit-content;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: 700;

      &_inactive {
        cursor: auto;
        border: none;
        padding-left: 0;
      }

      &__list {
        cursor: default;
        position: absolute;
        left: 0;
        top: 100%;
        background: $white;
        z-index: 1;
        box-shadow: 0px 0px 8px rgba(153, 153, 153, 0.25);
        border-radius: 4px;
        padding: 7px 17px 17px;
      }

      &__item {
        cursor: pointer;
        margin-top: 10px;
        display: flex;
        align-items: center;

        svg {
          margin-left: 9px;
          margin-right: 8px;
        }

        input {
          cursor: pointer;
          width: 20px;
          height: 20px;
        }
      }

      &__icon-down {
        transition: rotate 0.3s;
        stroke: $primary;
      }

      &.active {
        .dropdown__icon-down {
          rotate: 180deg;
        }
      }

      &[disabled="true"] {
        pointer-events: none;

        .dropdown__icon-down {
          display: none;
        }
      }
    }
  }

  &__loyalty-wrapper {
    position: relative;
  }

  &__remove-button {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 15px;
    height: 15px;
    display: inline-flex;
    color: $primary;

    &:hover {
      color: $color;
    }
  }

  &__bottom-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__refunded {
    font-size: 14px;
    color: $primary;
  }

  @media (max-width: $screen-xs) {
    width: 100%;
    max-width: inherit;
    min-height: auto;

    &__content {
      flex-grow: 1;
      text-align: left;
      padding: 20px;
      align-items: flex-start;
      min-height: auto;
    }

    &__remove {
      &-button {
        &:after {
          filter: brightness(100%);
        }
      }
    }

    &__place {
      width: 100%;
    }
  }
}
</style>
